import { useMermaidSvg } from '@/components/artifact/useMermaidSvg';
import { useTheme } from '@/components/theme/ThemeProvider';
import { CopyButton } from '@/components/ui/copy-button';
import { copySvgAsPng, readThemeSurface } from '@/lib/svg-image';
import { Check, ImageDown, Maximize2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { MermaidViewerModal } from './MermaidViewerModal';

interface Props {
  raw: string;
}

/**
 * 채팅(및 위키) 본문에 인라인으로 표시되는 mermaid 다이어그램.
 *
 * - 성공: 테마 표면 카드에 자연 크기 SVG 렌더(축소 없이 글자 가독성 유지). 폭이 넘치면 가로
 *   스크롤(스크롤바는 hover 시 전역 CSS 로 노출). hover 툴바에서 이미지 복사 / 확대 모달.
 * - 실패/스트리밍 미완성: 빨간 에러 박스 대신 일반 코드블록으로 폴백. 펜스가 완성되면 자연
 *   스럽게 다이어그램으로 전환.
 */
export function InlineMermaid({ raw }: Props) {
  const { resolved } = useTheme();
  const { svg, error, cleaned } = useMermaidSvg(raw, { dark: resolved === 'dark' });
  const [modalOpen, setModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyImage = useCallback(async () => {
    if (!svg) return;
    const ok = await copySvgAsPng(svg, { background: readThemeSurface() });
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }, [svg]);

  if (error || !svg) {
    return (
      <div className="group/code relative my-3">
        <pre className="overflow-x-auto rounded-md bg-code-bg p-3 font-mono text-[13px] leading-6">
          <code>{cleaned}</code>
        </pre>
        <CopyButton
          text={cleaned}
          label="코드 복사"
          className="absolute right-2 top-2 bg-code-bg opacity-0 group-hover/code:opacity-100 group-focus-within/code:opacity-100"
        />
      </div>
    );
  }

  const iconBtn =
    'grid size-7 place-items-center rounded-md bg-surface/90 text-foreground-muted shadow-sm hover:bg-surface-muted hover:text-foreground';

  return (
    <>
      <div className="group/mermaid relative my-3">
        <div className="max-h-[60vh] overflow-auto rounded-md border border-border-muted bg-surface p-4 [&_svg]:h-auto [&_svg]:max-w-none">
          {/* biome-ignore lint/security/noDangerouslySetInnerHtml: mermaid.render 가 생성한 SVG. */}
          <div dangerouslySetInnerHTML={{ __html: svg }} />
        </div>
        <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 transition-opacity group-hover/mermaid:opacity-100 group-focus-within/mermaid:opacity-100">
          <button
            type="button"
            onClick={handleCopyImage}
            aria-label="이미지 복사"
            title={copied ? '복사됨' : '이미지 복사'}
            className={iconBtn}
          >
            {copied ? <Check className="size-4 text-accent" /> : <ImageDown className="size-4" />}
          </button>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            aria-label="확대해서 보기"
            title="확대해서 보기"
            className={iconBtn}
          >
            <Maximize2 className="size-4" />
          </button>
        </div>
      </div>
      <MermaidViewerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        svg={svg}
        source={cleaned}
      />
    </>
  );
}
